import { useEffect, useState } from "react";
import API from "../../api/api";
import Modal from "../../components/Modal";
import Toast from "../../components/Toast";
import FormInput from "../../components/FormInput";
import ImageUpload from "../../components/ImageUpload";
import "./EventsList.css";

const IMG_BASE = "https://cmsapi.mks.org.au/uploads/";

function EventsList() {
    const [events, setEvents] = useState([]);
    const [filteredEvents, setFilteredEvents] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [viewOpen, setViewOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [existingImages, setExistingImages] = useState([]);
    const [formData, setFormData] = useState({
        eventId: null,
        title: "",
        description: "",
        eventDate: "",
        location: "",
        registrationEnabled: true,
        bookingUrl: "",
        galleryEventId: "",
        images: []
    });
    const [activeTab, setActiveTab] = useState("upcoming");
    const [galleries, setGalleries] = useState([]);
    const [toast, setToast] = useState(null);

    const fetchEvents = async () => {
        setLoading(true);
        try {
            const [upcoming, past] = await Promise.all([
                API.get("/events/upcoming"),
                API.get("/events/past")
            ]);

            const now = new Date();
            now.setHours(0, 0, 0, 0); // Match SQL DATE logic

            const upcomingList = (upcoming.data || []).map(e => ({ 
                ...e, 
                status: "Upcoming" 
            }));

            const pastList = (past.data || []).map(e => ({ 
                ...e, 
                status: "Past" 
            }));

            const allEvents = [...upcomingList, ...pastList];
            
            // Sort by Date (Upcoming: Closest first, Past: Latest first)
            allEvents.sort((a, b) => {
                if (a.status === "Upcoming" && b.status === "Upcoming") return new Date(a.EventDate) - new Date(b.EventDate);
                return new Date(b.EventDate) - new Date(a.EventDate);
            });

            setEvents(allEvents);
            setFilteredEvents(allEvents);
        } catch (err) {
            console.error("Error fetching events:", err);
            showToast("Error fetching events", "error");
        } finally {
            setLoading(false);
        }
    };

    const fetchGalleries = async () => {
        try {
            const response = await API.get("/gallery");
            setGalleries(response.data || []);
        } catch (err) {
            console.error("Error fetching galleries:", err);
        }
    };

    useEffect(() => {
        fetchEvents();
        fetchGalleries();
    }, []);

    useEffect(() => {
        const results = events.filter(x =>
            x.Title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            x.Location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            x.Description?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredEvents(results);
    }, [searchTerm, events]);

    const handleOpenModal = (event = null, isOnlyPhotoUpload = false) => {
        if (event) {
            setEditMode(!isOnlyPhotoUpload);
            // Split existing images and set to state
            const images = (event.ImageFile && typeof event.ImageFile === 'string')
                ? event.ImageFile.split(',').filter(img => img.trim() !== "")
                : [];
            setExistingImages(images);

            setFormData({
                eventId: event.EventId,
                title: event.Title,
                description: event.Description,
                eventDate: event.EventDate ? event.EventDate.split('.')[0] : "",
                location: event.Location,
                registrationEnabled: event.RegistrationEnabled,
                bookingUrl: event.BookingUrl || "",
                galleryEventId: event.GalleryEventId || "",
                images: []
            });
        } else {
            setEditMode(false);
            setExistingImages([]);
            setFormData({
                eventId: null,
                title: "",
                description: "",
                eventDate: "",
                location: "",
                registrationEnabled: true,
                bookingUrl: "",
                galleryEventId: "",
                images: []
            });
        }
        setIsModalOpen(true);
    };

    const removeExistingImage = (img) => {
        setExistingImages(prev => prev.filter(i => i !== img));
    };

    const viewEvent = (event) => {
        setSelectedEvent(event);
        setViewOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = new FormData();
            if (formData.images && formData.images.length > 0) {
                Array.from(formData.images).forEach(file => {
                    data.append("images", file);
                });
            }

            // JOIN THE REMAINING EXISTING IMAGES BACK INTO A COMMA-SEPARATED STRING
            const updatedImageFile = existingImages.join(",");

            const params = new URLSearchParams({
                title: formData.title,
                description: formData.description,
                eventDate: formData.eventDate,
                location: formData.location,
                registrationEnabled: formData.registrationEnabled,
                bookingUrl: formData.bookingUrl,
                galleryEventId: formData.galleryEventId,
                // PASS THE UPDATED IMAGE LIST SO BACKEND KNOWS WHAT TO KEEP
                existingImages: updatedImageFile
            }).toString();

            console.log("Submitting Event Payload:", { ...formData, existingImages: updatedImageFile });

            let response;
            if (formData.eventId) {
                console.log(`[Admin] Updating event ${formData.eventId}...`);
                response = await API.put(`/events/${formData.eventId}?${params}`, data, {
                    headers: { "Content-Type": "multipart/form-data" }
                });
                console.log("[Admin] Update Response:", response.data);
                showToast("Event updated successfully");
            } else {
                console.log("[Admin] Creating new event...");
                response = await API.post(`/events?${params}`, data, {
                    headers: { "Content-Type": "multipart/form-data" }
                });
                console.log("[Admin] Create Response:", response.data);
                showToast("Event created successfully");
            }
            setIsModalOpen(false);
            fetchEvents();
        } catch (err) {
            console.error("[Admin] Error saving event:", err);
            console.error("[Admin] Error details:", err.response?.data || err.message);
            showToast(err.response?.data || "Error saving event", "error");
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this event?")) {
            try {
                await API.delete(`/events/${id}`);
                showToast("Event deleted successfully");
                fetchEvents();
            } catch (err) {
                showToast("Error deleting event", "error");
            }
        }
    };

    const showToast = (message, type = "success") => {
        setToast({ message, type });
    };

    return (
        <div className="admin-card animate-fade-in">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "35px", flexWrap: "wrap", gap: "20px" }}>
                <div>
                    <h2 style={{ margin: 0, color: "var(--text-dark)", fontSize: "1.75rem", fontWeight: "800" }}>Manage Events</h2>
                    <p style={{ margin: "5px 0 0 0", color: "var(--text-muted)", fontSize: "0.9rem" }}>Schedule and organize community events and celebrations</p>
                </div>
                <div style={{ display: "flex", gap: "15px", alignItems: "center", flex: 1, justifyContent: "flex-end" }}>
                    <div className="search-container">
                        <span className="search-icon">🔍</span>
                        <input
                            className="form-control"
                            placeholder="Search by title, location..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button className="btn btn-primary" onClick={() => handleOpenModal()} style={{ height: "45px" }}>
                        + Create New Event
                    </button>
                </div>
            </div>

            <div className="admin-tabs" style={{ display: "flex", gap: "30px", marginBottom: "30px", borderBottom: "1px solid #e2e8f0" }}>
                <button 
                    className={`tab-btn ${activeTab === "upcoming" ? "active" : ""}`} 
                    onClick={() => setActiveTab("upcoming")}
                    style={{ 
                        padding: "12px 5px", border: "none", background: "none", cursor: "pointer",
                        fontWeight: "600", fontSize: "1rem", color: activeTab === "upcoming" ? "var(--primary)" : "var(--text-muted)",
                        borderBottom: activeTab === "upcoming" ? "3px solid var(--primary)" : "3px solid transparent",
                        transition: "all 0.3s"
                    }}
                >
                    Upcoming Events ({events.filter(x => x.status === "Upcoming").length})
                </button>
                <button 
                    className={`tab-btn ${activeTab === "past" ? "active" : ""}`} 
                    onClick={() => setActiveTab("past")}
                    style={{ 
                        padding: "12px 5px", border: "none", background: "none", cursor: "pointer",
                        fontWeight: "600", fontSize: "1rem", color: activeTab === "past" ? "var(--primary)" : "var(--text-muted)",
                        borderBottom: activeTab === "past" ? "3px solid var(--primary)" : "3px solid transparent",
                        transition: "all 0.3s"
                    }}
                >
                    Past Events ({events.filter(x => x.status === "Past").length})
                </button>
            </div>

            {loading ? (
                <div style={{ textAlign: "center", padding: "100px" }}>
                    <div className="spinner"></div>
                    <p style={{ marginTop: "15px", color: "var(--text-muted)" }}>Loading events...</p>
                </div>
            ) : (
                <div className="event-card-grid">
                    {filteredEvents.filter(x => activeTab === "all" || x.status.toLowerCase() === activeTab).length === 0 ? (
                        <div style={{ gridColumn: "1- / -1", textAlign: "center", padding: "60px", background: "#f8fafc", borderRadius: "20px" }}>
                            <p style={{ fontSize: "1.1rem", color: "var(--text-muted)" }}>No {activeTab} events found matching your criteria.</p>
                        </div>
                    ) : (
                        filteredEvents
                            .filter(x => activeTab === "all" || x.status.toLowerCase() === activeTab)
                            .map(x => {
                                const images = (x.ImageFile && typeof x.ImageFile === 'string') ? x.ImageFile.split(',').filter(Boolean) : [];
                                const firstImg = images[0];
                                const imgUrl = firstImg ? (firstImg.startsWith('http') ? firstImg : IMG_BASE + firstImg) : "https://via.placeholder.com/400x300?text=No+Image";

                                return (
                                    <div key={x.EventId} className="event-card">
                                        <div className="event-card-image-wrapper">
                                            <img src={imgUrl} className="event-card-img" alt={x.Title} />
                                            <span className={`event-status-tag ${x.status === "Upcoming" ? "status-upcoming" : "status-past"}`}>
                                                {x.status}
                                            </span>
                                            {x.RegistrationEnabled && (
                                                <div className="registration-indicator">
                                                    <span>📝</span> Registration Open
                                                </div>
                                            )}
                                        </div>

                                        <div className="event-card-content">
                                            <h3 className="event-card-title">{x.Title}</h3>
                                            
                                            <div className="event-meta-info">
                                                <div className="meta-item">
                                                    <span className="meta-icon">📅</span>
                                                    <span>{x.EventDate ? new Date(x.EventDate).toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}</span>
                                                </div>
                                                <div className="meta-item">
                                                    <span className="meta-icon">📍</span>
                                                    <span>{x.Location}</span>
                                                </div>
                                            </div>

                                            <p className="event-card-description">{x.Description}</p>
                                        </div>

                                        <div className="event-card-footer">
                                            <div className="event-card-actions">
                                                <button className="btn btn-outline" onClick={() => viewEvent(x)}>View</button>
                                                <button className="btn btn-outline" onClick={() => handleOpenModal(x)}>Edit</button>
                                                {x.status === "Past" && (
                                                    <button className="btn btn-primary" onClick={() => handleOpenModal(x, true)}>+ Photos</button>
                                                )}
                                                <button 
                                                    className="btn btn-outline" 
                                                    onClick={() => handleDelete(x.EventId)} 
                                                    style={{ color: "var(--danger)", borderColor: "rgba(239, 68, 68, 0.2)" }}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                    )}
                </div>
            )}

            {/* Create/Edit Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={editMode ? "Edit Event" : (formData.eventId ? "Add Event Photos" : "Create New Event")}
            >
                <form onSubmit={handleSubmit} className="premium-form">
                    {!editMode && formData.eventId ? (
                        <p style={{ color: "var(--text-muted)", marginBottom: "20px" }}>Adding new photos to the gallery for: <strong>{formData.title}</strong></p>
                    ) : (
                        <>
                            <FormInput label="Event Title" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} required />
                            <div className="form-group">
                                <label>Description</label>
                                <textarea className="form-control" rows="4" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} required style={{ padding: '12px' }} />
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                                <FormInput label="Event Date" type="datetime-local" value={formData.eventDate} onChange={e => setFormData({ ...formData, eventDate: e.target.value })} required />
                                <FormInput label="Location" value={formData.location} onChange={e => setFormData({ ...formData, location: e.target.value })} required />
                            </div>
                            <FormInput label="Booking Link (URL)" value={formData.bookingUrl} onChange={e => setFormData({ ...formData, bookingUrl: e.target.value })} placeholder="https://..." />
                            <div className="form-group" style={{ display: "flex", alignItems: "center", gap: "12px", background: "#f8fafc", padding: "12px", borderRadius: "12px" }}>
                                <input type="checkbox" id="reg-toggle" checked={formData.registrationEnabled} onChange={e => setFormData({ ...formData, registrationEnabled: e.target.checked })} style={{ width: "18px", height: "18px" }} />
                                <label htmlFor="reg-toggle" style={{ marginBottom: 0, cursor: "pointer" }}>Enable Online Registration</label>
                            </div>
                            <div className="form-group">
                                <label>Linked Gallery Album (Optional)</label>
                                <select 
                                    className="form-control" 
                                    value={formData.galleryEventId} 
                                    onChange={e => setFormData({ ...formData, galleryEventId: e.target.value })}
                                    style={{ padding: '12px' }}
                                >
                                    <option value="">-- No Gallery Linked --</option>
                                    {galleries.map(g => (
                                        <option key={g.GalleryEventId} value={g.GalleryEventId}>
                                            {g.Title} ({g.Year})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </>
                    )}

                    {/* Previews of existing images */}
                    {existingImages.length > 0 && (
                        <div className="existing-gallery-preview" style={{ marginBottom: "25px" }}>
                            <label style={{ fontSize: "0.75rem", textTransform: "uppercase", fontWeight: "700", color: "var(--text-muted)", display: "block", marginBottom: "10px" }}>Existed Photos</label>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
                                {existingImages.map((img, i) => (
                                    <div key={i} style={{ position: "relative", width: "80px", height: "80px" }}>
                                        <img
                                            src={img.startsWith('http') ? img : IMG_BASE + img}
                                            style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "12px", border: "2px solid #e2e8f0" }}
                                            alt="Existed"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeExistingImage(img)}
                                            style={{
                                                position: "absolute", top: "-8px", right: "-8px", width: "24px", height: "24px",
                                                borderRadius: "50%", background: "#ef4444", color: "white",
                                                border: "none", display: "flex", alignItems: "center", justifyContent: "center",
                                                cursor: "pointer", fontSize: "14px", fontWeight: "bold", zIndex: 2
                                            }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* New Images Section */}
                    <div className="form-group">
                        <label>Event Images (Gallery)</label>
                        <ImageUpload multiple onChange={files => setFormData({ ...formData, images: files })} label="Drop images here (Cover + Gallery)" />

                        {/* Preview of new images adding now */}
                        {formData.images && formData.images.length > 0 && (
                            <div style={{ marginTop: "15px" }}>
                                <label style={{ fontSize: "0.75rem", textTransform: "uppercase", fontWeight: "700", color: "#6366f1", display: "block", marginBottom: "10px" }}>Adding New Photos</label>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
                                    {Array.from(formData.images).map((file, i) => (
                                        <div key={i} style={{ width: "80px", height: "80px", position: "relative" }}>
                                            <img
                                                src={URL.createObjectURL(file)}
                                                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "12px", border: "2px solid #6366f1" }}
                                                alt="New preview"
                                            />
                                            <div style={{ position: "absolute", bottom: "0", left: "0", right: "0", background: "rgba(99, 102, 241, 0.8)", color: "white", fontSize: "8px", textAlign: "center", borderRadius: "0 0 10px 10px", padding: "2px" }}>NEW</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div style={{ marginTop: "30px", display: "flex", justifyContent: "flex-end", gap: "12px" }}>
                        <button type="button" className="btn btn-outline" onClick={() => setIsModalOpen(false)}>Cancel</button>
                        <button type="submit" className="btn btn-primary">
                            {formData.eventId ? (editMode ? "Save Changes" : "Upload Photos") : "Create Event"}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* View Details Modal */}
            <Modal
                isOpen={viewOpen}
                onClose={() => setViewOpen(false)}
                title="Event Details"
            >
                {selectedEvent && (
                    <div className="event-details-view" style={{ color: "var(--text-dark)" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px" }}>
                            <h2 style={{ margin: 0, fontSize: "1.75rem", fontWeight: "800" }}>{selectedEvent.Title}</h2>
                            <span className={`badge`} style={{
                                background: selectedEvent.status === "Upcoming" ? "rgba(16, 185, 129, 0.1)" : "rgba(100, 116, 139, 0.1)",
                                color: selectedEvent.status === "Upcoming" ? "#10b981" : "#64748b",
                                padding: "6px 16px",
                                borderRadius: "30px"
                            }}>
                                {selectedEvent.status}
                            </span>
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", marginBottom: "24px", background: "#f8fafc", padding: "20px", borderRadius: "20px" }}>
                            <div>
                                <label style={{ fontSize: "0.75rem", textTransform: "uppercase", fontWeight: "700", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>Date & Time</label>
                                <p style={{ margin: 0, fontWeight: "600" }}>{new Date(selectedEvent.EventDate).toLocaleString()}</p>
                            </div>
                            <div>
                                <label style={{ fontSize: "0.75rem", textTransform: "uppercase", fontWeight: "700", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>Location</label>
                                <p style={{ margin: 0, fontWeight: "600" }}>{selectedEvent.Location}</p>
                            </div>
                        </div>

                        <div style={{ marginBottom: "30px" }}>
                            <label style={{ fontSize: "0.75rem", textTransform: "uppercase", fontWeight: "700", color: "var(--text-muted)", display: "block", marginBottom: "8px" }}>About the Event</label>
                            <p style={{ margin: 0, lineHeight: "1.6" }}>{selectedEvent.Description}</p>
                        </div>

                        <div>
                            <label style={{ fontSize: "0.75rem", textTransform: "uppercase", fontWeight: "700", color: "var(--text-muted)", display: "block", marginBottom: "15px" }}>Event Gallery</label>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
                                {selectedEvent.ImageFile && typeof selectedEvent.ImageFile === 'string' ? (
                                    selectedEvent.ImageFile.split(',').map((img, i) => (
                                        <img
                                            key={i}
                                            src={img.startsWith('http') ? img : IMG_BASE + img}
                                            style={{ width: "calc(33.33% - 8px)", height: "100px", objectFit: "cover", borderRadius: "12px", border: "1px solid var(--border)", cursor: "pointer" }}
                                            alt={`Gallery ${i}`}
                                            onClick={() => window.open(img.startsWith('http') ? img : IMG_BASE + img, '_blank')}
                                        />
                                    ))
                                ) : (
                                    <p style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>No gallery images available.</p>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        </div>
    );
}

export default EventsList;